import type { Category } from "@/lib/types";
import { validColor } from "@/lib/color";
import type { ProfileForm } from "../form";

const MAX_INTRO = 1000;

interface BizFieldsProps {
  form: ProfileForm;
  onChange: (key: keyof ProfileForm, value: string | boolean) => void;
  categories: Category[];
  disabled?: boolean;
}

const inputCls =
  "w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-background-100";
const labelCls = "mb-1.5 block text-sm font-semibold text-foreground-700";
const req = <span className="text-accent-600">*</span>;

// 사업장 정보 입력
export default function BizFields({
  form,
  onChange,
  categories,
  disabled = false,
}: BizFieldsProps) {
  const selected = categories.find((c) => c.code === form.biz_cate);
  const selectedColor = validColor(selected?.color) || "#17408B";
  const over = form.biz_intro.length > MAX_INTRO;

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="font-heading text-base font-bold text-foreground-900">사업장 정보</h2>

      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="biz_name" className={labelCls}>
              직장명 {req}
            </label>
            <input
              id="biz_name"
              type="text"
              value={form.biz_name}
              onChange={(e) => onChange("biz_name", e.target.value)}
              disabled={disabled}
              placeholder="직장명을 입력하세요"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="biz_position" className={labelCls}>
              직책
            </label>
            <input
              id="biz_position"
              type="text"
              value={form.biz_position}
              onChange={(e) => onChange("biz_position", e.target.value)}
              disabled={disabled}
              placeholder="예: 대표이사"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="biz_cate" className={labelCls}>
              업종
            </label>
            <div className="flex items-center gap-2">
              <select
                id="biz_cate"
                value={form.biz_cate}
                onChange={(e) => onChange("biz_cate", e.target.value)}
                disabled={disabled}
                className={inputCls}
              >
                <option value="">선택 안 함</option>
                {categories.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              {selected && (
                <span
                  className="shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold"
                  style={{ borderColor: selectedColor, color: selectedColor }}
                >
                  {selected.name}
                </span>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="biz_type" className={labelCls}>
              업종 표기
            </label>
            <input
              id="biz_type"
              type="text"
              value={form.biz_type}
              onChange={(e) => onChange("biz_type", e.target.value)}
              disabled={disabled}
              placeholder="예) 금속공업 · 임대유통업 (비우면 업종명이 표시됩니다)"
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label htmlFor="biz_tel" className={labelCls}>
            사업장 전화
          </label>
          <input
            id="biz_tel"
            type="tel"
            value={form.biz_tel}
            onChange={(e) => onChange("biz_tel", e.target.value)}
            disabled={disabled}
            placeholder="예: 042-123-4567"
            className={inputCls}
          />
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="biz_addr" className={labelCls}>
              직장주소 {req}
            </label>
            <input
              id="biz_addr"
              type="text"
              value={form.biz_addr}
              onChange={(e) => onChange("biz_addr", e.target.value)}
              disabled={disabled}
              placeholder="도로명 주소를 입력하세요"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="biz_addr2" className={labelCls}>
              상세주소
            </label>
            <input
              id="biz_addr2"
              type="text"
              value={form.biz_addr2}
              onChange={(e) => onChange("biz_addr2", e.target.value)}
              disabled={disabled}
              placeholder="층 · 호수 등"
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label htmlFor="biz_intro" className={labelCls}>
            사업장 소개
          </label>
          <textarea
            id="biz_intro"
            rows={5}
            maxLength={MAX_INTRO}
            value={form.biz_intro}
            onChange={(e) => onChange("biz_intro", e.target.value)}
            disabled={disabled}
            placeholder="사업장 소개를 입력하세요"
            className={`${inputCls} resize-y`}
          />
          <p
            className={`mt-1.5 text-right text-xs ${
              over ? "font-semibold text-accent-700" : "text-foreground-400"
            }`}
          >
            {form.biz_intro.length} / {MAX_INTRO}
          </p>
        </div>

        <div>
          <label htmlFor="biz_url" className={labelCls}>
            홈페이지 / SNS
          </label>
          <input
            id="biz_url"
            type="url"
            value={form.biz_url}
            onChange={(e) => onChange("biz_url", e.target.value)}
            disabled={disabled}
            placeholder="https://"
            className={inputCls}
          />
        </div>
      </div>
    </section>
  );
}